import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject} from 'rxjs';
import { MenuMode } from '../interfaces/menu-mode';
import { MenuState } from '../constants/menu-state.enum';
import { MenuItem } from '../interfaces/menu-item';
import { AccountsService } from './accounts.service';
import { IntegrationsService } from './integrations.service';
import { Account } from '@mitel/cloudlink-sdk';
import { RBACAuthGuardService } from './../guards/rbac-auth-guard.service';
import { EventHistoryFilterRequirements, UserRoles, productCodes } from './../constants/constants';
import _ from 'lodash';

// Consider moving this Getter to a separate file.
// This is a huge file and it would be better to
// separate the getter logic from the service logic.
// See https://github.com/mitel-networks/cloudlink-console-accounts/compare/Olivia-Split-Menu-Service
export namespace ModeGetter {
    function sortChildren(parentMenuItem: MenuItem): void {
        // This is the order in which child items should be sorted
        // The keys are the parent menu item IDs,
        // and the values are arrays of child item IDs in the desired order
        const ChildItemOrder = {
            'MiVoiceBusiness': [
                'SystemSettings',
                'Numbers',
                'DepartmentsAndLocations',
                'GroupManagement',
                'QueueManagement',
                'EmergencyServices',
                'VoiceGateway',
                'Tools',
            ],
            'support': [
                'supportLogs',
                'eventHistory',
                'gatewaySupport',
                'zappaSupport',
                'supportReportProblem',
            ]
        };
        if (parentMenuItem?.children?.length > 1 && parentMenuItem?.id in ChildItemOrder) {
            // Sort the children based on the order defined in ChildItemOrder
            const order = ChildItemOrder[parentMenuItem.id];
            parentMenuItem.children.sort((a, b) => {
                return order.indexOf(a.id) - order.indexOf(b.id);
            });
        }
    }

	export function getMenuItems(
        mode: MenuState, 
        id?: string, 
        hasBillingAccount?: boolean, 
        hasManagedAccounts?: boolean,
        hasAccountDelegation?: boolean,
        featureSpecificMenus?: MenuItem[],
        isPartnerPrimeAccount?: boolean,
        hasAPIDeveloperMenu?: boolean,
        hasGWPIntegration?: boolean,
        hasMiVBOnboarded?: boolean,
        isSubPartner?: boolean,
        hasPartnerUserTemplateAccess?: boolean,
        hasMiCCBIntegration?: boolean,
        hasSystemInventoryAccess?: boolean,
        hasZappaIntegration?: boolean,
        hasManagementProfilesAccess?: boolean): MenuItem[]
    {
        const partnerDashboardItem = {
            name: 'Dashboard',
            translationKey: 'Dashboard',
            id: 'dashboard',
            minRole: UserRoles.USER,
            navigation: '/',
            iconActive: '../../../../assets/images/dashboard_active.svg',
            iconInactive: '../../../../assets/images/dashboard_inactive.svg'
        };
        const accountDashboardItem = {
            name: 'Dashboard',
            translationKey: 'Dashboard',
            id: 'dashboard',
            minRole: UserRoles.USER,
            navigation: `/dashboard/${id}`,
            iconActive: '../../../../assets/images/dashboard_active.svg',
            iconInactive: '../../../../assets/images/dashboard_inactive.svg'
        };
        const accountsItem = {
            name: 'Accounts',
            translationKey: 'Accounts',
            id: 'accounts',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: '/accounts',
            iconActive: '../../../../assets/images/account_active.svg',
            iconInactive: '../../../../assets/images/account_inactive.svg'
        };

        const accountManagementItem = {
            name: 'AccountManagement',
            translationKey: 'AccountManagers',
            id: 'accountManagement',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: `/account-management`,
            iconActive: '',
            iconInactive: ''
        };

        const rolesManagementItem = {
             name: 'RolesManagement',
             translationKey: 'RolesManagement',
             id: 'rolesManagement',
             minRole: UserRoles.PARTNER_ADMIN,
             navigation: `/roles`,
             isFederatedSPA: true
        };

        const ordersItem = {
            name: 'Orders',
            translationKey: 'Orders',
            id: 'orders',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: '/orders'
        };

        const developerMenuItem = {
            name: 'Developer',
            translationKey: 'Developer',
            id: 'developer',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: '/developer',
            iconActive: '../../../../assets/images/developer_active.svg', // need to replace these icons
            iconInactive: '../../../../assets/images/developer_inactive.svg'
        };

        const billingItem = {
            name: 'Billing',
            translationKey: 'Billing',
            id: 'billing',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: '/norouting',
            iconActive: '../../../../assets/images/billing_active.svg',
            iconInactive: '../../../../assets/images/billing_inactive.svg',
            children: [
                ordersItem
            ]
        };

        const platformsItem = {
          name: 'Platforms',
          translationKey: 'Platforms',
          id: 'platforms',
          minRole: UserRoles.PARTNER_ADMIN,
          navigation: `/platforms`,
          isFederatedSPA: true
        };

        const applicationsItem = {
          name: 'Applications',
          translationKey: 'Applications',
          id: 'applications',
          minRole: UserRoles.PARTNER_ADMIN,
          navigation: `/applications`,
          isFederatedSPA: true
        };

        const systemInventoryItem = {
          name: 'SystemInventory',
          translationKey: 'SystemInventory',
          id: 'systemInventory',
          minRole: UserRoles.PARTNER_ADMIN,
          navigation: '/norouting',
          iconActive: '../../../../assets/images/system_inventory_active.svg',
          iconInactive: '../../../../assets/images/system_inventory_inactive.svg',
          children: [
            platformsItem,
            applicationsItem
          ]
        };

        const subscriptionsItem = {
            name: 'Subscriptions',
            translationKey: 'Subscriptions',
            id: 'subscriptions',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/subscriptions`
        };

        const accountBillingItem = {
            name: 'AccountBilling',
            translationKey: 'Billing',
            id: 'accountBilling',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: '/norouting',
            iconActive: '../../../../assets/images/billing_active.svg',
            iconInactive: '../../../../assets/images/billing_inactive.svg',
            children: [
                subscriptionsItem
            ]
        };

        const accountItem = {
            name: 'Account',
            translationKey: 'Account',
            id: 'account',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}`,
            iconActive: '../../../../assets/images/account_active.svg',
            iconInactive: '../../../../assets/images/account_inactive.svg'
        };

        const usersItem = {
            name: 'Users',
            translationKey: 'Users',
            id: 'users',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/users`
        };

        const customerRolesItem = {
            name: 'RolesManagement',
            translationKey: 'RolesManagement',
            id: 'rolesManagement',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/roles`,
            isFederatedSPA: true
        };

        const customerUserManagementItem = {
            name: 'customerUserManagement',
            translationKey: 'UserManagement',
            id: 'customerUserManagement',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: '/norouting',
            iconActive: '../../../../assets/images/users_active.svg',
            iconInactive: '../../../../assets/images/users_inactive.svg',
            children: [
                usersItem
            ]
        };
        
        const managementProfileItem = {
          name: 'ManagementProfiles',
          translationKey: 'ManagementProfiles',
          id: 'managementProfiles',
          minRole: UserRoles.ACCOUNT_ADMIN,
          navigation: `/accounts/${id}/management-profiles`,
          isFederatedSPA: true
      };

        const managedAccountsItem = {
            name: 'ManagedAccounts',
            translationKey: 'ManagedAccounts',
            id: 'ManagedAccounts',
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: `/managed-accounts`,
            iconActive: '',
            iconInactive: ''
        };

        const userManagementItem = {
            name: 'UserManagement',
            translationKey: 'UserManagement',
            id: "userManagement",
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: "/norouting",
            iconActive: '../../../../assets/images/users_active.svg',
            iconInactive: '../../../../assets/images/users_inactive.svg',
            children: []
        };

        const supportLogsItem = {
            name: 'SupportLogs',
            translationKey: 'SupportLogs',
            id: 'supportLogs',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/support-logs`,
            isFederatedSPA: true
        };

        const eventHistoryItem = {
            name: 'EventHistory',
            translationKey: 'EventHistory',
            id: 'eventHistory',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/event-history`,
            isFederatedSPA: true
        };

        const supportReportProblemItem = {
            name: 'SupportReportProblem',
            translationKey: 'ReportProblem',
            id: 'supportReportProblem',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: '/report-a-problem',
            isFederatedSPA: false
        };

        const gatewaySupportItem = {
            name: 'GatewaySupport',
            translationKey: 'GatewaySupport',
            id: 'gatewaySupport',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/gatewaysupport`,
            isFederatedSPA: false,
        };

        const supportItem = {
            name: 'Support',
            translationKey: 'Support',
            id: 'support',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: '/norouting',
            iconActive: '../../../../assets/images/supportlogs_active.svg',
            iconInactive: '../../../../assets/images/supportlogs_inactive.svg',
            children: [
                supportLogsItem,
                supportReportProblemItem
            ]
        };
        
        const supportSearchItem = {
            name: 'Support Search',
            translationKey: 'SupportSearch',
            id: 'support-search',
            minRole: UserRoles.GLOBAL_ADMIN,
            navigation: '/support-search',
            iconActive: '../../../../assets/images/supportsearch_active.svg',
            iconInactive: '../../../../assets/images/supportsearch_inactive.svg'
        };

        const featureFlagsItem = {
          name: 'Feature Flags',
          translationKey: 'FeatureFlags',
          id: 'feature-flags',
          minRole: UserRoles.GLOBAL_ADMIN ,
          navigation: '/feature-flags'
        };

        const templateManagementItem = {
            name: 'UserTemplates',
            translationKey: 'UserTemplates',
            id: "UserTemplates",
            minRole: UserRoles.PARTNER_ADMIN,
            navigation: "/user-templates",
            iconActive: '',
            iconInactive: '',
            isFederatedSPA: true,
        };

        const accountTemplateManagementItem = {
            name: 'UserTemplates',
            translationKey: 'UserTemplates',
            id: "UserTemplates",
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/user-templates`,
            iconActive: '',
            iconInactive: '',
            isFederatedSPA: true,
        };

        const integrationAppsItem = {
            name: 'IntegrationApps',
            translationKey: 'IntegrationApps',
            id: 'integrationApps',
            minRole: UserRoles.ACCOUNT_ADMIN,
            navigation: `/accounts/${id}/integrations`,
            iconActive: '../../../../assets/images/integrations_active.svg',
            iconInactive: '../../../../assets/images/integrations_inactive.svg'
        };        

        const customerPlatformsItem = {
          name: 'Platforms',
          translationKey: 'Platforms',
          id: 'platforms',
          minRole: UserRoles.ACCOUNT_ADMIN,
          navigation: `/accounts/${id}/platforms`,
          isFederatedSPA: true
        };

        const customerApplicationsItem = {
          name: 'Applications',
          translationKey: 'Applications',
          id: 'applications',
          minRole: UserRoles.ACCOUNT_ADMIN,
          navigation: `/accounts/${id}/applications`,
          isFederatedSPA: true
        };

        const customerSystemInventoryItem = {
          name: 'SystemInventory',
          translationKey: 'SystemInventory',
          id: 'SystemInventory',
          minRole: UserRoles.ACCOUNT_ADMIN,
          navigation: '/norouting',
          iconActive: '../../../../assets/images/system_inventory_active.svg',
          iconInactive: '../../../../assets/images/system_inventory_inactive.svg',
          children: [
            customerPlatformsItem,
            customerApplicationsItem
          ]
        };

        let menuItems = [];
		switch (mode) {
			case MenuState.PartnerAdmin:
			{
          menuItems = [
            partnerDashboardItem,
            accountsItem
          ];
          if (hasAccountDelegation) {
            userManagementItem.children.push(accountManagementItem);
          }
          if (hasManagedAccounts) {
            userManagementItem.children.push(managedAccountsItem);
          }

          userManagementItem.children.push(rolesManagementItem);

          //only if Prime account has the partner access policy
          if (hasPartnerUserTemplateAccess) {
            userManagementItem.children.push(templateManagementItem);
          }

          if (userManagementItem.children.length > 0) {
            menuItems.push(userManagementItem);
          }
          if (hasBillingAccount) {
            menuItems.push(billingItem);
          }
          if (hasAPIDeveloperMenu) {
            menuItems.push(developerMenuItem);
          }
          if(hasSystemInventoryAccess) {
            menuItems.push(systemInventoryItem);
          }
          break;
			}
			case MenuState.AccountAdmin:
            {

                customerUserManagementItem.children.push(customerRolesItem);

				menuItems =	[
                    accountDashboardItem,
                    accountItem,
                    customerUserManagementItem
                ];
                if ( !isPartnerPrimeAccount )
                    menuItems.push(integrationAppsItem);

                if ( !isPartnerPrimeAccount && hasBillingAccount )
                {
                    menuItems.push(accountBillingItem);
                }
                else if (isSubPartner == true)
                {
                    menuItems.push(accountBillingItem);
                }

                if(hasGWPIntegration){
                    supportItem.children.push(gatewaySupportItem);
                }

                if (hasMiVBOnboarded || hasZappaIntegration) {
                    let eventHistoryItemWithRouteData = _.cloneDeep(eventHistoryItem);
                    if(hasMiVBOnboarded && hasZappaIntegration) {
                        //pass along info about which requirement was satisfied in order to show the menu. 
                        //When Event History loads, it will be looking for an array of satisfiedRequirements
                        eventHistoryItemWithRouteData.routeData = { 
                            shell: 'accounts', 
                            satisfiedRequirements: [EventHistoryFilterRequirements.MIVB_CAP, EventHistoryFilterRequirements.ZAPPA] 
                        };
                    }
                    else if(hasMiVBOnboarded){
                        eventHistoryItemWithRouteData.routeData = { 
                            shell: 'accounts', 
                            satisfiedRequirements: [EventHistoryFilterRequirements.MIVB_CAP] 
                        };
                    }
                    else{
                        eventHistoryItemWithRouteData.routeData = { 
                            shell: 'accounts', 
                            satisfiedRequirements: [EventHistoryFilterRequirements.ZAPPA] 
                        };
                    }

                    supportItem.children.splice(1,0, eventHistoryItemWithRouteData);
                }

                // Push support item before adding feature specific menus
                menuItems.push(supportItem);

                if ( featureSpecificMenus && featureSpecificMenus.length > 0 ) {
                    featureSpecificMenus.forEach((featureSpecificMenuItem) => {
                        if (featureSpecificMenuItem.parentId) {
                            // Check if the parent menu item exists and if the child menu item is not already present
                            let parentMenuItem = menuItems.find(menuItem => menuItem.id === featureSpecificMenuItem.parentId);
                            if (parentMenuItem && !parentMenuItem.children.some(existingChild => existingChild.id === featureSpecificMenuItem.id)) {
                                parentMenuItem.children.push(featureSpecificMenuItem);
                            }
                        } else {
                            // Check if a menu item with the same ID already exists
                            let existingMenuItem = menuItems.find(menuItem => menuItem.id === featureSpecificMenuItem.id);
                            if (existingMenuItem) {
                                // Merge children if they are not already present
                                featureSpecificMenuItem.children?.forEach((child) => {
                                    if (!existingMenuItem.children.some(existingChild => existingChild.id === child.id)) {
                                        existingMenuItem.children.push(child);
                                    }
                                });
                                sortChildren(existingMenuItem);
                            } else {
                                // Add the menu item to the root level if it doesn't already exist
                                menuItems.push(featureSpecificMenuItem);
                            }
                        }
                    });
                }
                
                /* 
                 If Partner_Admin, mivb policy must be on Prime
                 If Account_Admin, mivb policy must be on account
                 Customer account must have Gateway integration with MiVB onboarded
                 OR
                 If Account_Admin, Customer account must have MiCCB integration
                */
                if(hasMiVBOnboarded || hasMiCCBIntegration) {
                    customerUserManagementItem.children.push(accountTemplateManagementItem);
                }
                
                /* 
                 Customer account must have Gateway integration with MiVB onboarded
                 and must have Management Profiles feature flag
                */
                if (hasMiVBOnboarded && hasManagementProfilesAccess) {
                  customerUserManagementItem.children.push(managementProfileItem);
                }

                if(hasSystemInventoryAccess) {
                  menuItems.push(customerSystemInventoryItem);
                }

                // Support menu item MUST be last
                if (supportItem) {
                    menuItems.splice(menuItems.indexOf(supportItem), 1);
                    menuItems.push(supportItem);
                }

                break;
      }
      case MenuState.GlobalAdmin:
      {
          menuItems = [
              supportSearchItem
          ];
          break;
      }
      case MenuState.FeatureFlagsAdmin:
      {
        menuItems = [
          featureFlagsItem
        ];
        break;
      }
    }
    return menuItems;
  }
}

@Injectable({
	providedIn: 'root'
})

export class MenuService {
	currentState = MenuState.PartnerAdmin;
	private _initialRoute = [];
    private hasBillingAccount: boolean = false;
    private hasManagedAccounts = false;
    private hasAccountDelegation = false;
    private hasAPIDeveloperMenu: boolean = false;
    private isPartnerPrimeAccount: boolean = false;
    private hasGWPPolicy: boolean = false;
    private hasGWPIntegration: boolean = false;
    private featureSpecificMenus: MenuItem[] = [];
    private hasMiVBOnboarded: boolean = false;
    private isSubPartner: boolean = false;
    private hasPartnerUserTemplateAccess: boolean = false;
    private hasSystemInventoryAccess: boolean = false;
    private hasMiCCBIntegration: boolean = false;
    private hasZappaIntegration: boolean = false;
    private hasManagementProfilesAccess: boolean = false;
    menuItemsUpdatedInSideNav: Subject<any[]> = new Subject<any[]>();
    	
	get initialRoute(): any[] {
		return this._initialRoute;
	}

	set initialRoute(value: any[]) {
		this._initialRoute = value;
	}

	private menuStateTracker = new BehaviorSubject<MenuMode>({
		state: this.rbacAuthGuardService.filterMenuByPermissions(ModeGetter.getMenuItems(
            this.currentState, 
            undefined, 
            this.hasBillingAccount, 
            undefined, 
            this.hasAccountDelegation,
            undefined, 
            this.isPartnerPrimeAccount,
            this.hasAPIDeveloperMenu,
            this.hasGWPPolicy && this.hasGWPIntegration,
            this.hasMiVBOnboarded,
            this.isSubPartner,
            this.hasPartnerUserTemplateAccess,
            this.hasMiCCBIntegration,
            this.hasSystemInventoryAccess,
            this.hasZappaIntegration,
            this.hasManagementProfilesAccess
            )
        )
	});

	private _isSwitcherProcessingRequired = false;

	get isSwitcherProcessingRequired(): boolean {
		return this._isSwitcherProcessingRequired;
	}

	set isSwitcherProcessingRequired(value: boolean) {
		this._isSwitcherProcessingRequired = value;
	}

	setBillingAccountState( hasAccount: boolean )
	{
        this.hasBillingAccount = hasAccount;
    }

    setSubPartnerState( subPartner: boolean )
	{
        this.isSubPartner = subPartner;
    }
    
    setManagedAccountsAccess(access: boolean) {
        this.hasManagedAccounts = access;
    }

    setAccountDelegationAccess(access: boolean) {
        this.hasAccountDelegation = access;
    }
        
    setGWPPolicy(hasPolicy: boolean) {
        this.hasGWPPolicy = hasPolicy;
    }
    
    setGWPIntegration(hasIntegration: boolean) {
        this.hasGWPIntegration = hasIntegration;
    }

    get GWPPolicy(): boolean{
        return this.hasGWPPolicy;
    }

    setAPIDeveloperAccess(access: boolean) {
        this.hasAPIDeveloperMenu = access;
    }

    setIsPartnerPrimeAccount( prime: boolean) {
        this.isPartnerPrimeAccount = prime;
    }

    setMiVBOnboarded(onboarded: boolean) {
        this.hasMiVBOnboarded = onboarded;
    }

    setPartnerUserTemplateAccess(onboarded: boolean) {
        this.hasPartnerUserTemplateAccess = onboarded;
    }

    setMiCCBIntegration(hasIntegration: boolean) {
        this.hasMiCCBIntegration = hasIntegration;
    }

    setSystemInventoryAccess(hasAccess: boolean) {
       this.hasSystemInventoryAccess = hasAccess;
    }

    setZappaIntegration(hasIntegration: boolean) {
        this.hasZappaIntegration = hasIntegration;
    }

    setManagementProfilesAccess(hasAccess: boolean) {
        this.hasManagementProfilesAccess = hasAccess;
    }

    constructor( private accountsService: AccountsService,
        private integService: IntegrationsService,
        private rbacAuthGuardService: RBACAuthGuardService) {}

	menuStateChanged(): Observable<any> {
		return this.menuStateTracker.asObservable();
	}

	getMenuState(): MenuState {
		return this.currentState;
	}

	setState(state: MenuState, account_id?: string, navigateTo?: string[]): void {
        console.log("set menu state ", state, account_id, navigateTo )
        this.currentState = state;
        this.menuStateTracker.next({ 
            state: this.rbacAuthGuardService.filterMenuByPermissions(ModeGetter.getMenuItems( 
                <MenuState>state, 
                account_id, 
                this.hasBillingAccount, 
                this.hasManagedAccounts, 
                this.hasAccountDelegation,
                this.featureSpecificMenus, 
                this.isPartnerPrimeAccount,
                this.hasAPIDeveloperMenu,
                this.hasGWPPolicy && this.hasGWPIntegration,
                this.hasMiVBOnboarded,
                this.isSubPartner,
                this.hasPartnerUserTemplateAccess,
                this.hasMiCCBIntegration,
                this.hasSystemInventoryAccess,
                this.hasZappaIntegration,
                this.hasManagementProfilesAccess
                )
            ),
            id: account_id, 
            route: navigateTo});
    }
    
    // we are doing this outside "getMenuItems" so it does not slow down the display of the menu
    clearFeatureSpecificMenusForAccount()
    {
        this.featureSpecificMenus = [];
    }

    checkForFeatureSpecificMenusForAccount( account: Account): Promise<boolean>
    {
        this.featureSpecificMenus = [];
        this.setGWPIntegration(false);
        this.setMiCCBIntegration(false);
        this.setMiVBOnboarded(false);
        this.setZappaIntegration(false);
        // for now all the feature specific menus are integration based which is tag based
        return this.accountsService.getAccountTagsById(account.accountId).then( async tags => {
            console.debug("These are the tags", tags);
            if ( tags && tags.products )
            {
                let mitelFeatures = this.integService.getMitelIntegrationList(account);
                let thirdPartyFeatures = this.integService.getThirdPartyIntegrationList(account);
                console.debug("These are the integrations", mitelFeatures, thirdPartyFeatures);
                for ( var i = 0; i < tags.products.length; i++ ) 
                {
                    let elem = tags.products[i];
                    console.log("looking for the integration", elem)
                    if ( this.integService.isAnAccountBasedIntegration(elem) === true )
                    {
                        let prodDesc = [...mitelFeatures, ...thirdPartyFeatures].find( item => {
                            return item.productCode == elem;
                        });
                        if ( prodDesc && prodDesc.service && prodDesc.service.getLeftNavMenus )
                        {
                            let menus = await prodDesc.service.getLeftNavMenus(account);
                            if ( menus && menus.length > 0)
                            {
                                for ( var j = 0; j < menus.length; j++ )
                                {
                                        this.featureSpecificMenus.push(menus[j]);
                                }

                                // here we will change the conditions of when hasMiVBOnboarded is set
                                // this is used for user templates/management profiles for the customer accounts
                                // the new requirements are that the CAP policy is enabled. It will only
                                // be tested if the MiVB integration is present. If the integration is MiVB and 
                                // there are menus then CAP is enabled and so should the hasMiVBOnboarded flag
                                // not liking having to check this here. When this service is refactored we need to find
                                // a way that allows feature menus to be embedded into the main stream menus
                                if(elem === productCodes.MiVB_INTEGRATION) {
                                    this.setMiVBOnboarded(true);
                                }

                                if(elem === productCodes.ZAPPA) {
                                    this.setZappaIntegration(true);
                                }
                            }
                        }
                        if (elem === productCodes.MiCCB) {
                            this.setMiCCBIntegration(true);
                        }
                    }
                    
                    if(elem === productCodes.GATEWAY){
                        this.setGWPIntegration(true);
                    }
                }
                return (this.featureSpecificMenus.length > 0 ? true : false );
            }
            return false;
        })
        .catch(err =>
        {
            console.error("There was an error requesting tags for account Id", account.accountId, err);
            return false;
        });
    }

    getMenuItemById( accountId: string, menuId: string ): any
    {
        let menus = this.rbacAuthGuardService.filterMenuByPermissions(ModeGetter.getMenuItems( 
            <MenuState>this.currentState, 
            accountId, 
            this.hasBillingAccount, 
            this.hasManagedAccounts, 
            this.hasAccountDelegation,
            this.featureSpecificMenus, 
            this.isPartnerPrimeAccount,
            this.hasAPIDeveloperMenu,
            this.hasGWPPolicy && this.hasGWPIntegration,
            this.hasMiVBOnboarded,
            this.isSubPartner,
            this.hasPartnerUserTemplateAccess,
            this.hasMiCCBIntegration,
            this.hasSystemInventoryAccess,
            this.hasZappaIntegration,
            this.hasManagementProfilesAccess
            )
        );

        let menuItem = undefined;
        for ( var i = 0; i < menus.length; i++ )
        {
            if ( menus[i].id == menuId )
                return menus[i];

            if ( menus[i].children && menus[i].children.length > 0 )
            {
                menuItem = menus[i].children.find(item => item.id === menuId);
                if ( menuItem != undefined )
                    return menuItem;
            }
        }

        return menuItem;
    }
}